import { create } from 'zustand';
import type { Agent } from '@/types/agent';
import type { Room } from '@/types/room';
import type { Store } from '@/types/store';
import type { Task } from '@/types/task';
import type { ActivityEvent } from '@/types/event';
import type { GlobalMetrics } from '@/types/metrics';
import { ROOMS } from '@/data/rooms';
import { INITIAL_STORES } from '@/data/stores';
import { INITIAL_TASKS } from '@/data/tasks';
import { buildInitialAgents, INITIAL_METRICS, INITIAL_EVENTS } from '@/data/initialState';
import { supabase } from '@/datasource/supabaseClient';

const MAX_EVENTS = 100;
const LIVE = (import.meta.env.VITE_DATA_SOURCE as string) === 'live';

/** Persiste la décision de validation dans Supabase (mode live) via RPC sécurisé. */
function persistDecision(taskId: string, decision: 'approve' | 'reject', reason?: string) {
  if (!LIVE || !supabase) return;
  const fn = decision === 'approve' ? 'approve_task' : 'reject_task';
  void supabase.rpc(fn, { p_task_id: taskId, p_reason: reason ?? null })
    .then(({ error }: { error: unknown }) => { if (error) console.error('[persistDecision]', error); });
}

interface SimulationState {
  agents: Agent[];
  rooms: Room[];
  stores: Store[];
  tasks: Task[];
  events: ActivityEvent[];
  metrics: GlobalMetrics;
  killSwitchActive: boolean;

  // ── Setters (utilisés par le moteur de simulation) ────────────────────
  setAgents: (agents: Agent[]) => void;
  updateAgent: (id: string, patch: Partial<Agent>) => void;
  setRooms: (rooms: Room[]) => void;
  updateRoom: (id: string, patch: Partial<Room>) => void;
  setStores: (stores: Store[]) => void;
  setTasks: (tasks: Task[]) => void;
  setEvents: (events: ActivityEvent[]) => void;
  setMetrics: (metrics: GlobalMetrics) => void;
  patchMetrics: (patch: Partial<GlobalMetrics>) => void;
  addEvent: (event: ActivityEvent) => void;
  updateTask: (id: string, patch: Partial<Task>) => void;
  addTask: (task: Task) => void;

  // ── Actions utilisateur ───────────────────────────────────────────────
  approveTask: (taskId: string) => void;
  rejectTask: (taskId: string, reason?: string) => void;
  assignAgent: (agentId: string, storeId: string) => void;
  toggleKillSwitch: () => void;
  markEventsRead: () => void;
}

export const useSimulationStore = create<SimulationState>((set, get) => ({
  agents: buildInitialAgents(),
  rooms: ROOMS,
  stores: INITIAL_STORES,
  tasks: INITIAL_TASKS,
  events: INITIAL_EVENTS,
  metrics: INITIAL_METRICS,
  killSwitchActive: false,

  setAgents: (agents) => set({ agents }),

  updateAgent: (id, patch) =>
    set(s => ({
      agents: s.agents.map(a => (a.id === id ? { ...a, ...patch } : a)),
    })),

  setRooms: (rooms) => set({ rooms }),

  // Setters de remplacement complet (utilisés par le bridge Supabase / live)
  setStores: (stores) => set({ stores }),
  setTasks: (tasks) => set({ tasks }),
  setEvents: (events) => set({ events }),

  updateRoom: (id, patch) =>
    set(s => ({
      rooms: s.rooms.map(r => (r.id === id ? { ...r, ...patch } : r)),
    })),

  setMetrics: (metrics) => set({ metrics }),

  patchMetrics: (patch) =>
    set(s => ({ metrics: { ...s.metrics, ...patch } })),

  addEvent: (event) =>
    set(s => ({
      events: [event, ...s.events].slice(0, MAX_EVENTS),
    })),

  updateTask: (id, patch) =>
    set(s => ({
      tasks: s.tasks.map(t => (t.id === id ? { ...t, ...patch } : t)),
    })),

  addTask: (task) =>
    set(s => ({ tasks: [task, ...s.tasks] })),

  approveTask: (taskId) => {
    const { updateTask, updateAgent, addEvent, tasks, agents } = get();
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    updateTask(taskId, { status: 'done', completedAt: Date.now(), progress: 100 });
    persistDecision(taskId, 'approve');
    const agent = agents.find(a => a.id === task.agentId);
    if (agent) {
      updateAgent(agent.id, { status: 'working', currentTaskId: null });
    }
    addEvent({
      id: `ev_approve_${Date.now()}`,
      type: 'task_done',
      agentId: task.agentId,
      storeId: task.storeId,
      message: `Tâche approuvée — ${task.title}`,
      timestamp: Date.now(),
      read: false,
    });
  },

  rejectTask: (taskId, reason) => {
    const { updateTask, addEvent, tasks } = get();
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    updateTask(taskId, { status: 'in_progress', progress: Math.max(0, (task.progress || 100) - 20) });
    persistDecision(taskId, 'reject', reason);
    addEvent({
      id: `ev_reject_${Date.now()}`,
      type: 'alert',
      agentId: task.agentId,
      storeId: task.storeId,
      message: `Tâche rejetée — ${task.title}${reason ? ` · ${reason}` : ''}`,
      timestamp: Date.now(),
      read: false,
    });
  },

  assignAgent: (agentId, storeId) => {
    set(s => ({
      stores: s.stores.map(st =>
        st.id === storeId
          ? { ...st, assignedAgentIds: [...new Set([...st.assignedAgentIds, agentId])] }
          : st
      ),
    }));
  },

  toggleKillSwitch: () =>
    set(s => ({ killSwitchActive: !s.killSwitchActive })),

  markEventsRead: () =>
    set(s => ({ events: s.events.map(e => ({ ...e, read: true })) })),
}));
